import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { useAuth, API } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { Upload, Loader2, ExternalLink, Image as ImageIcon, Link as LinkIcon, Check } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function EmployerCareerEditor() {
  const { t } = useI18n();
  const { user, authHeader } = useAuth();
  const [form, setForm] = useState({ about: "", website: "", industry: "", headquarters: "", logo_url: "", cover_url: "", contact_email: "", contact_phone: "", address: "" });
  const [loading, setLoading] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [copied, setCopied] = useState(false);

  const publicKey = user?.slug || user?.id;
  const publicUrl = `${window.location.origin}/career/${publicKey}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      toast.success(t("employer.linkCopied"));
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast.error("Copy failed");
    }
  };

  useEffect(() => {
    axios.get(`${API}/employer/career`, { headers: authHeader }).then(r => setForm({
      about: r.data.about || "",
      website: r.data.website || "",
      industry: r.data.industry || "",
      headquarters: r.data.headquarters || "",
      logo_url: r.data.logo_url || "",
      cover_url: r.data.cover_url || "",
      contact_email: r.data.contact_email || "",
      contact_phone: r.data.contact_phone || "",
      address: r.data.address || "",
    }));
    // eslint-disable-next-line
  }, []);

  const upload = async (kind, file) => {
    if (kind === "logo") setUploadingLogo(true); else setUploadingCover(true);
    try {
      const fd = new FormData();
      fd.append("kind", kind); fd.append("file", file);
      const r = await axios.post(`${API}/employer/career/upload`, fd, { headers: { ...authHeader, "Content-Type": "multipart/form-data" } });
      setForm(prev => ({ ...prev, [kind === "logo" ? "logo_url" : "cover_url"]: r.data.url }));
      toast.success("Uploaded");
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    if (kind === "logo") setUploadingLogo(false); else setUploadingCover(false);
  };

  const save = async () => {
    setLoading(true);
    try {
      await axios.patch(`${API}/employer/career`, { about: form.about, website: form.website, industry: form.industry, headquarters: form.headquarters, contact_email: form.contact_email, contact_phone: form.contact_phone, address: form.address }, { headers: authHeader });
      toast.success("Saved");
    } catch (e) { toast.error("Save failed"); }
    setLoading(false);
  };

  const fullUrl = (u) => u?.startsWith("http") ? u : `${BACKEND_URL}${u}`;

  return (
    <div className="space-y-6" data-testid="career-editor">
      <Card className="overflow-hidden">
        {/* Cover preview */}
        <div className="relative h-44 bg-slate-100">
          {form.cover_url ? (
            <img src={fullUrl(form.cover_url)} alt="Cover" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-slate-400"><ImageIcon className="h-8 w-8" /></div>
          )}
          <label className="absolute top-3 right-3 inline-flex items-center gap-1 bg-black/70 hover:bg-black text-white text-xs font-medium rounded-full px-3 py-1.5 cursor-pointer transition-colors">
            {uploadingCover ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
            {t("employer.uploadCover")}
            <input type="file" accept="image/*" className="hidden" data-testid="upload-cover" onChange={(e) => e.target.files?.[0] && upload("cover", e.target.files[0])} />
          </label>
          {/* Logo */}
          <div className="absolute -bottom-10 left-6 h-20 w-20 rounded-2xl border-4 border-white bg-white shadow overflow-hidden">
            {form.logo_url ? (
              <img src={fullUrl(form.logo_url)} alt="Logo" className="w-full h-full object-contain" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-slate-400"><ImageIcon className="h-5 w-5" /></div>
            )}
          </div>
        </div>
        <div className="pt-14 px-6 pb-6 flex items-center justify-between">
          <div>
            <h2 className="font-display text-xl font-bold text-slate-900">{user?.company_name}</h2>
            <p className="text-sm text-slate-500">{form.industry || "—"} {form.headquarters ? `· ${form.headquarters}` : ""}</p>
          </div>
          <div className="flex gap-2">
            <label className="inline-flex items-center gap-1 border border-slate-200 hover:bg-slate-50 rounded-full px-3 py-1.5 text-xs cursor-pointer">
              {uploadingLogo ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
              {t("employer.uploadLogo")}
              <input type="file" accept="image/*" className="hidden" data-testid="upload-logo" onChange={(e) => e.target.files?.[0] && upload("logo", e.target.files[0])} />
            </label>
            <a href={`/career/${publicKey}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-[#1A8FE3] hover:underline" data-testid="view-public-page">
              <ExternalLink className="h-3 w-3" />{t("employer.viewPublic")}
            </a>
            <button type="button" onClick={copyLink} data-testid="copy-career-link" className="inline-flex items-center gap-1 border border-slate-200 hover:bg-slate-50 rounded-full px-3 py-1.5 text-xs">
              {copied ? <Check className="h-3 w-3 text-emerald-600" /> : <LinkIcon className="h-3 w-3" />}
              {copied ? t("employer.linkCopied") : t("employer.copyLink")}
            </button>
          </div>
        </div>
      </Card>

      <Card className="p-6 space-y-4">
        <p className="text-sm text-slate-500">{t("employer.careerDesc")}</p>
        <div className="grid grid-cols-2 gap-4">
          <div><Label>{t("employer.industry")}</Label><Input data-testid="career-industry" value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} /></div>
          <div><Label>{t("employer.headquarters")}</Label><Input data-testid="career-hq" value={form.headquarters} onChange={(e) => setForm({ ...form, headquarters: e.target.value })} /></div>
        </div>
        <div><Label>{t("employer.website")}</Label><Input data-testid="career-website" placeholder="https://" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} /></div>
        <div><Label>{t("employer.about")}</Label><Textarea data-testid="career-about" rows={6} value={form.about} onChange={(e) => setForm({ ...form, about: e.target.value })} /></div>

        <div className="pt-2 border-t border-slate-100">
          <h3 className="font-display text-base font-semibold text-slate-900 mb-3">{t("employer.contactSection")}</h3>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>{t("employer.contactEmail")}</Label><Input data-testid="career-contact-email" type="email" placeholder="careers@company.mu" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} /></div>
            <div><Label>{t("employer.contactPhone")}</Label><Input data-testid="career-contact-phone" type="tel" placeholder="+230 ..." value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} /></div>
          </div>
          <div className="mt-4"><Label>{t("employer.address")}</Label><Textarea data-testid="career-address" rows={2} placeholder="Street, City, Mauritius" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
        </div>

        <Button onClick={save} disabled={loading} data-testid="career-save" className="bg-[#1A8FE3] hover:bg-[#157AC0] text-white rounded-full">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("employer.save")}
        </Button>
      </Card>
    </div>
  );
}
